import { Component } from '@angular/core';
import { AlertController, LoadingController, NavController, NavParams, ToastController } from 'ionic-angular';
import { TranslateService } from '@ngx-translate/core';
import { Globals } from '../../../../globals';
import { OdsServices } from '../../services';
import { OdsConfigMenuCreatePage } from '../ods-config-menu/ods-config-menu-create/ods-config-menu-create';

/**
 * Generated class for the OdsConfigModifyElementsPage page.
 *
 * See https://ionicframework.com/docs/components/#navigation for more info on
 * Ionic pages and navigation.
 */

@Component({
    selector: 'page-ods-config-modify-elements',
    templateUrl: 'ods-config-modify-elements.html',
})
export class OdsConfigModifyElementsPage {
    // Tracking de cambios: Map<elementUID, Set<camposModificados>>
    private modifiedElements: Map<string, Set<string>> = new Map();
    // Valores originales para comparar: Map<elementUID, valoresOriginales>
    private originalValues: Map<string, any> = new Map();
    // Estado de guardado
    public isSaving: boolean = false;
    public savingProgress: { current: number, total: number } = { current: 0, total: 0 };
    public showProgressCard: boolean = false;
    public showPreparingCard: boolean = false;
    // Protección de inputs con focus: Set<elementUID_fieldName>
    private editingInputs: Set<string> = new Set();
    // Valores locales de inputs con focus para preservarlos durante actualizaciones del listener
    private localInputValues: Map<string, any> = new Map();
    // Listener personalizado para manejar actualizaciones de forma inteligente
    private customListener: any = null;

    constructor(public navCtrl: NavController,
        public navParams: NavParams,
        public translate: TranslateService,
        public globals: Globals,
        public toastCtrl: ToastController,
        public odsServices: OdsServices,
        public loadingCtrl: LoadingController,
        public alertCtrl: AlertController) {

    }

    ionViewWillEnter() {
        this.globals.GAtrackView('Ods', 'Ods - Modify - Elements', this.globals.restaurant.email, 'View', 'Modify - Elements', 'Ods', '');
        // Iniciar listener personalizado que protege inputs con focus
        this.startProtectedListener();
        // Inicializar tracking de valores originales
        this.initializeOriginalValues();
    }

    ionViewWillLeave() {
        // Detener listener personalizado al salir
        this.stopProtectedListener();
        // Detener listener del servicio si está activo
        this.odsServices.stopCartaTakeawayListener();
    }

    /**
     * Inicializa los valores originales de todos los elementos para poder detectar cambios
     */
    private initializeOriginalValues() {
        this.originalValues.clear();
        this.modifiedElements.clear();
        
        this.globals.objectToArray(this.odsServices.carta_takeaway.pages[0].sections).forEach(section => {
            this.globals.objectToArray(section.elements).forEach(element => {
                if (element.uid) {
                    this.originalValues.set(element.uid, {
                        price_takeaway: element.price_takeaway,
                        tax: element.tax,
                        takeaway_units: element.takeaway_units,
                        featured: element.featured,
                        tk_active: element.tk_active
                    });
                }
            });
        });
    }

    /**
     * Marca un campo como modificado para un elemento
     */
    private markFieldAsModified(elementUid: string, fieldName: string) {
        if (!this.modifiedElements.has(elementUid)) {
            this.modifiedElements.set(elementUid, new Set());
        }
        this.modifiedElements.get(elementUid).add(fieldName);
    }

    /**
     * Verifica si un campo fue modificado comparando con el valor original
     */
    private isFieldModified(elementUid: string, fieldName: string, currentValue: any): boolean {
        const original = this.originalValues.get(elementUid);
        if (!original) {
            // Si no hay valor original, considerar como modificado si tiene valor
            return currentValue !== undefined && currentValue !== null && currentValue !== '';
        }
        
        const originalValue = original[fieldName];
        
        // Normalizar para comparación
        if (fieldName === 'takeaway_units') {
            const normalizedOriginal = this.normalizeTakeawayUnits(originalValue);
            const normalizedCurrent = this.normalizeTakeawayUnits(currentValue);
            return normalizedOriginal !== normalizedCurrent;
        }
        
        // Para números, convertir ambos a número para comparar
        if (fieldName === 'price_takeaway' || fieldName === 'tax') {
            const origNum = originalValue === null || originalValue === undefined ? 0 : Number(originalValue);
            const currNum = currentValue === null || currentValue === undefined ? 0 : Number(currentValue);
            return origNum !== currNum;
        }
        
        // Comparación estricta para otros campos
        return originalValue !== currentValue;
    }

    async activeFeatured(element) {
        element.featured = true;
        await this.saveElementChanges(element);
        this.updateAllElementInstances(element);
    }

    async deactiveFeatured(element) {
        element.featured = false;
        await this.saveElementChanges(element);
        this.updateAllElementInstances(element);
    }

    async activeTk(element) {
        element.tk_active = true;
        await this.saveElementChanges(element);
        this.updateAllElementInstances(element);
    }

    async deactiveTk(element) {
        element.tk_active = false;
        await this.saveElementChanges(element);
        this.updateAllElementInstances(element);
    }

    /**
     * Normaliza el valor de takeaway_units
     * null/undefined/vacío = sin límite (null)
     * 0 = no se puede comprar (0)
     * número > 0 = límite de unidades
     */
    normalizeTakeawayUnits(value: any): number | null {
        if (value === null || value === undefined || value === '') {
            return null;
        }
        const numValue = Number(value);
        if (isNaN(numValue)) {
            return null;
        }
        return numValue;
    }

    updateAllElementInstances(element) {
        this.globals.objectToArray(this.odsServices.carta_takeaway.pages[0].sections).forEach(section => {
            this.globals.objectToArray(section.elements).forEach(elem => {
                if (elem.uid === element.uid) {
                    elem.featured = element.featured || false;
                    elem.tk_active = (element.tk_active === undefined) ? true : element.tk_active;
                    elem.price_takeaway = element.price_takeaway || 0;
                    elem.tax = (element.tax === undefined) ? this.globals.RESTAURANT_TAX : element.tax;
                    elem.takeaway_units = this.normalizeTakeawayUnits(element.takeaway_units);
                }
            });
        });
    }

    /**
     * Encuentra el índice del elemento en la carta
     */
    findElementIndex(element): { sectionIndex: number, elementIndex: number } | null {
        const sections = this.globals.objectToArray(this.odsServices.carta_takeaway.pages[0].sections);
        for (let sectionIndex = 0; sectionIndex < sections.length; sectionIndex++) {
            const section = sections[sectionIndex];
            const elements = this.globals.objectToArray(section.elements);
            for (let elementIndex = 0; elementIndex < elements.length; elementIndex++) {
                if (elements[elementIndex].uid === element.uid) {
                    return { sectionIndex, elementIndex };
                }
            }
        }
        return null;
    }

    /**
     * Guarda los cambios de un elemento específico en Firestore
     * Solo guarda featured y tk_active (para botones), no modifica otros campos
     * Incluye guardado del elemento individual y actualización de la carta completa
     */
    async saveElementChanges(element) {
        try {
            // 1. Actualizar solo los campos de botones (featured y tk_active)
            const updateData: any = {
                featured: element.featured || false,
                tk_active: (element.tk_active === undefined) ? true : element.tk_active
            };

            if (element.type === 'product') {
                await this.odsServices.firestore.collection('products').doc(element.uid).update(updateData);
            } else if (element.type === 'dish') {
                await this.odsServices.firestore.collection('dish').doc(element.uid).update(updateData);
            } else if (element.type === 'combo') {
                await this.odsServices.firestore.collection('menus').doc(element.uid).update(updateData);
            }

            // 2. Actualizar carta completa
            const cartaUpdate = { ...this.odsServices.carta_takeaway };
            delete cartaUpdate.UID;
            await this.odsServices.firestore.collection('cartas')
                .doc(this.odsServices.carta_takeaway.UID)
                .update(cartaUpdate);

            // 3. Feedback visual
            const toast = this.toastCtrl.create({
                message: this.translate.instant('instant-guardado-correctamente'),
                duration: 2000,
                position: 'bottom'
            });
            toast.present();
        } catch (error) {
            console.error('Error guardando cambios del elemento:', error);
            
            // Verificar si el error es porque el documento no existe
            if (error.message && error.message.includes('No document to update')) {
                // const elementIndex = this.findElementIndex(element);
                // if (elementIndex) {
                //     await this.showDocumentNotFoundAlert(element, elementIndex.sectionIndex, elementIndex.elementIndex);
                // } else {
                //     const toast = this.toastCtrl.create({
                //         message: 'Este elemento no existe en la base de datos. Por favor, elimínalo manualmente.',
                //         duration: 3000,
                //         position: 'bottom'
                //     });
                //     toast.present();
                // }
            } else {
                const toast = this.toastCtrl.create({
                    message: this.translate.instant('error-al-guardar'),
                    duration: 3000,
                    position: 'bottom'
                });
                toast.present();
            }
        }
    }

    /**
     * Muestra un alert cuando el documento no existe
     */
    async showDocumentNotFoundAlert(element, sectionIndex: number, elementIndex: number) {
        const alert = this.alertCtrl.create({
            title: this.translate.instant('general-atencion'),
            message: 'Este elemento no existe en la base de datos. ¿Deseas eliminarlo de la carta?',
            buttons: [
                {
                    text: this.translate.instant('general-cancelar'),
                    role: 'cancel'
                },
                {
                    text: this.translate.instant('general-eliminar'),
                    handler: () => {
                        this.deleteElement(sectionIndex, elementIndex);
                    }
                }
            ]
        });
        alert.present();
    }

    deleteElement(index_section, index_element) {
        this.odsServices.carta_takeaway.pages[0].sections[index_section].elements.splice(index_element, 1);
    }

    newElement(section, index) {
        this.navCtrl.push(OdsConfigMenuCreatePage, {
            update_state: true,
            section: section,
            index: index
        });
    }

    async save() {
        // Verificar si hay elementos modificados antes de cerrar
        if (this.modifiedElements.size === 0) {
            const toast = this.toastCtrl.create({
                message: 'No hay cambios para guardar',
                duration: 2000,
                position: 'bottom'
            });
            toast.present();
            this.navCtrl.pop();
            return;
        }
        
        // Detener listener personalizado temporalmente para evitar interferencias durante el guardado
        this.stopProtectedListener();
        
        // Bloquear acciones inmediatamente
        this.isSaving = true;
        
        // Contar elementos a guardar correctamente
        let totalElements = 0;
        for (const section of this.globals.objectToArray(this.odsServices.carta_takeaway.pages[0].sections)) {
            for (const element of this.globals.objectToArray(section.elements)) {
                const modifiedFields = this.modifiedElements.get(element.uid);
                if (modifiedFields && modifiedFields.size > 0) {
                    totalElements++;
                }
            }
        }
        
        // Inicializar progreso y mostrar card de "preparando datos"
        this.savingProgress = { current: 0, total: totalElements };
        this.showPreparingCard = true;
        this.showProgressCard = false;
        
        try {
            await this.updateElements();
            
            // Ocultar cards de progreso
            this.showProgressCard = false;
            this.showPreparingCard = false;
            
            // Reinicializar valores originales antes de cerrar (limpieza)
            this.initializeOriginalValues();
            
            // Mostrar mensaje de éxito
            const toast = this.toastCtrl.create({
                message: this.translate.instant('instant-guardado-correctamente'),
                duration: 2000,
                position: 'bottom'
            });
            toast.present();
            
            // Hacer pop inmediatamente sin esperar el callback para navegación más rápida
            // El listener se detendrá automáticamente en ionViewWillLeave()
            this.isSaving = false;
            this.navCtrl.pop(); // Sin await ni .then() para no bloquear la navegación
        } catch (error) {
            console.error(`[${this.getTimestamp()}] [SAVE] ❌ Error en save():`, error);
            this.showProgressCard = false;
            this.showPreparingCard = false;
            
            // Reiniciar listener personalizado incluso si hay error
            this.startProtectedListener();
            
            const toast = this.toastCtrl.create({
                message: this.translate.instant('error-al-guardar'),
                duration: 3000,
                position: 'bottom'
            });
            toast.present();
            this.isSaving = false;
        }
    }

    /**
     * Genera un timestamp formateado para los logs
     */
    private getTimestamp(): string {
        const now = new Date();
        const hours = ('0' + now.getHours()).slice(-2);
        const minutes = ('0' + now.getMinutes()).slice(-2);
        const seconds = ('0' + now.getSeconds()).slice(-2);
        const milliseconds = ('00' + now.getMilliseconds()).slice(-3);
        return `${hours}:${minutes}:${seconds}.${milliseconds}`;
    }

    /**
     * Actualiza el progreso del guardado
     */
    private updateSavingProgress() {
        // Si es el primer elemento guardado, cambiar de "preparando" a "guardando"
        if (this.savingProgress.current === 0 && this.showPreparingCard) {
            this.showPreparingCard = false;
            this.showProgressCard = true;
        }
        
        this.savingProgress.current++;
        // La card de progreso se actualiza automáticamente con el binding
    }
    
    /**
     * Calcula el porcentaje de progreso
     */
    getProgressPercentage(): number {
        if (this.savingProgress.total === 0) return 0;
        return Math.round((this.savingProgress.current / this.savingProgress.total) * 100);
    }

    /**
     * Guarda solo los elementos y campos que fueron modificados
     * Optimizado para guardar en paralelo usando Promise.all()
     */
    async updateElements() {
        let hasChanges = false;
        
        // Recopilar todos los elementos a guardar primero
        const elementsToUpdate: Array<{element: any, updateData: any, collectionName: string, fieldsList: string}> = [];
        
        for (const section of this.globals.objectToArray(this.odsServices.carta_takeaway.pages[0].sections)) {
            for (const element of this.globals.objectToArray(section.elements)) {
                const modifiedFields = this.modifiedElements.get(element.uid);
                
                // Solo procesar elementos que tienen campos modificados
                if (!modifiedFields || modifiedFields.size === 0) {
                    continue;
                }
                
                hasChanges = true;
                const fieldsList = Array.from(modifiedFields).join(', ');
                
                const updateData: any = {};
                
                // Solo agregar campos que fueron modificados
                if (modifiedFields.has('price_takeaway')) {
                    if (element.type === 'product') {
                        updateData.pvp_takeaway = element.price_takeaway || 0;
                    } else {
                        updateData.price_takeaway = element.price_takeaway || 0;
                    }
                }
                
                if (modifiedFields.has('tax')) {
                    updateData.tax = (element.tax === undefined) ? this.globals.RESTAURANT_TAX : element.tax;
                }
                
                if (modifiedFields.has('takeaway_units')) {
                    updateData.takeaway_units = this.normalizeTakeawayUnits(element.takeaway_units);
                }
                
                if (modifiedFields.has('featured')) {
                    updateData.featured = element.featured || false;
                }
                
                if (modifiedFields.has('tk_active')) {
                    updateData.tk_active = (element.tk_active === undefined) ? true : element.tk_active;
                }
                
                // Solo agregar si hay campos para actualizar
                if (Object.keys(updateData).length > 0) {
                    let collectionName = '';
                    if (element.type === 'product') {
                        collectionName = 'products';
                    } else if (element.type === 'dish') {
                        collectionName = 'dish';
                    } else if (element.type === 'combo') {
                        collectionName = 'menus';
                    }
                    
                    elementsToUpdate.push({ element, updateData, collectionName, fieldsList });
                }
            }
        }
        
        // Guardar elementos secuencialmente para mostrar progreso elemento por elemento
        if (elementsToUpdate.length > 0) {
            // Cambiar a card de progreso inmediatamente cuando empieza el guardado
            if (this.showPreparingCard) {
                this.showPreparingCard = false;
                this.showProgressCard = true;
            }
            
            const errors: Array<{index: number, error: any}> = [];
            
            // NOTA: La primera operación de Firestore puede tardar ~30-35 segundos debido a la inicialización
            // de la conexión. Esto es un problema conocido de Firestore y no se puede evitar desde el código.
            // Las operaciones siguientes deberían ser mucho más rápidas (~1-2 segundos cada una).
            
            // Guardar elementos uno por uno secuencialmente
            for (let i = 0; i < elementsToUpdate.length; i++) {
                const item = elementsToUpdate[i];
                
                try {
                    await this.odsServices.firestore.collection(item.collectionName)
                        .doc(item.element.uid)
                        .update(item.updateData);
                    
                    // Actualizar progreso después de cada elemento guardado
                    this.updateSavingProgress();
                } catch (error) {
                    console.error(`[${this.getTimestamp()}] [UPDATE_ELEMENTS] ❌ Error guardando elemento ${i + 1}/${elementsToUpdate.length} (${item.element.uid}):`, error);
                    
                    errors.push({ index: i, error });
                    
                    // Actualizar progreso incluso si hay error
                    this.updateSavingProgress();
                }
            }
            
            // Verificar si hubo errores
            if (errors.length > 0) {
                console.error(`[${this.getTimestamp()}] [UPDATE_ELEMENTS] ${errors.length} elementos tuvieron errores`);
                const toast = this.toastCtrl.create({
                    message: this.translate.instant('error-al-guardar'),
                    duration: 3000,
                    position: 'bottom'
                });
                toast.present();
            }
        }
        
        // Actualizar carta completa solo si hubo cambios
        if (hasChanges) {
            try {
                const cartaUpdate = { ...this.odsServices.carta_takeaway };
                delete cartaUpdate.UID;
                await this.odsServices.firestore.collection('cartas')
                    .doc(this.odsServices.carta_takeaway.UID)
                    .update(cartaUpdate);
            } catch (error) {
                console.error(`[${this.getTimestamp()}] [UPDATE_ELEMENTS] ❌ Error actualizando carta:`, error);
                const toast = this.toastCtrl.create({
                    message: this.translate.instant('error-al-guardar'),
                    duration: 3000,
                    position: 'bottom'
                });
                toast.present();
            }
        }
    }

    /**
     * Maneja el evento focus de los inputs para protegerlos de actualizaciones del listener
     */
    onInputFocus(element: any, fieldName: string) {
        if (!element || !element.uid) {
            return;
        }
        
        const inputKey = `${element.uid}_${fieldName}`;
        this.editingInputs.add(inputKey);
        // Guardar el valor actual para preservarlo si el listener actualiza
        this.localInputValues.set(inputKey, element[fieldName]);
    }

    /**
     * Maneja el evento blur de los inputs y marca el campo como modificado solo si realmente cambió
     */
    onInputBlur(element: any, fieldName: string) {
        if (!element || !element.uid) {
            return;
        }
        
        const inputKey = `${element.uid}_${fieldName}`;
        // Remover de la lista de inputs en edición
        this.editingInputs.delete(inputKey);
        this.localInputValues.delete(inputKey);
        
        const currentValue = element[fieldName];
        
        // Verificar si fue modificado comparando con el valor original
        if (this.isFieldModified(element.uid, fieldName, currentValue)) {
            this.markFieldAsModified(element.uid, fieldName);
        }
    }

    /**
     * Inicia un listener personalizado que protege los valores de inputs con focus
     */
    private startProtectedListener() {
        if (this.customListener) return;
        
        if (!this.odsServices.carta_takeaway || !this.odsServices.carta_takeaway.UID) {
            return;
        }

        this.customListener = this.odsServices.firestore
            .collection('cartas')
            .doc(this.odsServices.carta_takeaway.UID)
            .onSnapshot((doc) => {
                if (doc.exists) {
                    const data = doc.data();
                    data.UID = doc.id;
                    
                    // Preservar valores de inputs con focus antes de actualizar
                    // Usamos Map<elementUID_fieldName, valor> para preservar los valores
                    const preservedValues: Map<string, any> = new Map();
                    
                    if (this.editingInputs.size > 0 && this.odsServices.carta_takeaway.pages) {
                        this.globals.objectToArray(this.odsServices.carta_takeaway.pages[0].sections).forEach(section => {
                            this.globals.objectToArray(section.elements).forEach(element => {
                                if (element.uid) {
                                    ['price_takeaway', 'tax', 'takeaway_units'].forEach(fieldName => {
                                        const inputKey = `${element.uid}_${fieldName}`;
                                        if (this.editingInputs.has(inputKey)) {
                                            // Preservar el valor actual del input con focus usando el valor local guardado
                                            // Si hay un valor local guardado, usarlo; si no, usar el valor actual del elemento
                                            const preservedValue = this.localInputValues.has(inputKey) 
                                                ? this.localInputValues.get(inputKey) 
                                                : element[fieldName];
                                            preservedValues.set(inputKey, preservedValue);
                                        }
                                    });
                                }
                            });
                        });
                    }
                    
                    // Actualizar carta_takeaway con los datos remotos
                    this.odsServices.carta_takeaway = data;
                    
                    // Restaurar valores preservados de inputs con focus después de la actualización
                    if (preservedValues.size > 0 && this.odsServices.carta_takeaway.pages) {
                        this.globals.objectToArray(this.odsServices.carta_takeaway.pages[0].sections).forEach(section => {
                            this.globals.objectToArray(section.elements).forEach(element => {
                                if (element.uid) {
                                    ['price_takeaway', 'tax', 'takeaway_units'].forEach(fieldName => {
                                        const inputKey = `${element.uid}_${fieldName}`;
                                        if (preservedValues.has(inputKey)) {
                                            // Restaurar el valor preservado
                                            element[fieldName] = preservedValues.get(inputKey);
                                            // Actualizar también el valor local guardado
                                            this.localInputValues.set(inputKey, preservedValues.get(inputKey));
                                        }
                                    });
                                }
                            });
                        });
                    }
                }
            }, (error) => {
                console.error('Error en listener protegido de carta:', error);
            });
    }

    /**
     * Detiene el listener personalizado
     */
    private stopProtectedListener() {
        if (this.customListener) {
            this.customListener();
            this.customListener = null;
        }
        // Limpiar tracking de inputs en edición
        this.editingInputs.clear();
        this.localInputValues.clear();
    }

    onlyIntegers(event: KeyboardEvent) {
        const charCode = event.which ? event.which : event.keyCode;
        if (charCode == 44 || charCode == 45 ||charCode == 46) {
            event.preventDefault();
        }
    }

}


// WEBPACK FOOTER //
// ./src/pages/ods/ods-config/ods-config-modify-elements/ods-config-modify-elements.ts